import * as THREE from "three";
import type { Rng } from "../rng";
import { scatter, WORLD } from "../config";
import { flat, smooth, Tone, shade } from "../materials";
import { Slot, slotNormal } from "../palette";
import { fbm2, fbmPeriodic } from "../noise";
import { terrainHeight } from "./terrain";
import { group } from "../geometry";

const GROUND_TONES = [Tone.white, Tone.paper, Tone.bone, Tone.linen] as const;

export function groundToneAt(x: number, z: number): number {
  const patch = fbm2(x * 0.075, z * 0.075, 3, WORLD.seed + 41);
  const drift = fbm2(x * 0.28, z * 0.28, 2, WORLD.seed + 77);
  const mixed = patch * 0.78 + drift * 0.22;
  return Math.min(1, Math.max(0, (mixed - 0.5) * 1.85 + 0.5));
}

const toneFor = (level: number): number =>
  GROUND_TONES[
    Math.min(GROUND_TONES.length - 1, Math.max(0, Math.round(level * (GROUND_TONES.length - 1))))
  ] as number;

function groundDetailTexture(size: number, period: number): THREE.DataTexture {
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = (x / size) * period;
      const v = (y / size) * period;
      const coarse = fbmPeriodic(u * 1.6, v * 1.6, 4, period * 1.6, 9173);
      const fine = fbmPeriodic(u * 7.0, v * 7.0, 3, period * 7.0, 4421);
      const value = Math.min(1, Math.max(0, coarse * 0.62 + fine * 0.38));
      const modulated = 0.88 + value * 0.12;
      const byte = Math.round(modulated * 255);
      const index = (y * size + x) * 4;
      data[index] = byte;
      data[index + 1] = byte;
      data[index + 2] = byte;
      data[index + 3] = 255;
    }
  }
  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;
  return texture;
}

export function buildGround(): THREE.Mesh {
  const size = WORLD.groundSize;
  const segments = WORLD.groundSegments;
  const geometry = new THREE.PlaneGeometry(size, size, segments, segments);
  geometry.rotateX(-Math.PI / 2);

  const position = geometry.getAttribute("position") as THREE.BufferAttribute;
  const colors = new Float32Array(position.count * 3);
  const color = new THREE.Color();

  for (let i = 0; i < position.count; i++) {
    const x = position.getX(i);
    const z = position.getZ(i);
    position.setY(i, terrainHeight(x, z));

    color.set(toneFor(groundToneAt(x, z)));
    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;
  }

  position.needsUpdate = true;
  geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  geometry.computeVertexNormals();

  const detail = groundDetailTexture(128, 3);
  const tile = 26;
  detail.repeat.set(size / tile, size / tile);
  const material = new THREE.MeshLambertMaterial({
    vertexColors: true,
    flatShading: true,
    map: detail,
  });
  const ground = new THREE.Mesh(geometry, material);
  ground.receiveShadow = true;
  return ground;
}

const bladeGeometry = (height: number, width: number): THREE.BufferGeometry =>
  new THREE.CylinderGeometry(width * 0.16, width, height, 3, 1, false);

function tuftGeometry(rng: Rng): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const blades = rng.int(6, 11);
  for (let i = 0; i < blades; i++) {
    const height = rng.range(0.16, 0.36);
    const azimuth = (i / blades) * Math.PI * 2 + rng.range(-0.45, 0.45);
    const radius = rng.range(0.05, 0.1);
    const blade = bladeGeometry(height, radius);

    const lean = rng.range(0.25, 0.75);
    const bend = blade.clone();
    const bendPos = bend.getAttribute("position") as THREE.BufferAttribute;
    for (let v = 0; v < bendPos.count; v++) {
      const t = bendPos.getY(v) / height;
      bendPos.setX(v, bendPos.getX(v) + t * t * lean * 0.28);
    }
    bendPos.needsUpdate = true;

    bend.rotateY(azimuth);
    bend.translate(
      Math.cos(azimuth) * 0.03 + rng.range(-0.03, 0.03),
      0,
      Math.sin(azimuth) * 0.03 + rng.range(-0.03, 0.03),
    );
    parts.push(bend);
    blade.dispose();
  }

  const merged = mergeAll(parts);
  const normal = merged.getAttribute("normal") as THREE.BufferAttribute;
  for (let i = 0; i < normal.count; i++) {
    const nx = normal.getX(i) * 0.3;
    const ny = normal.getY(i) * 0.45 + 0.55;
    const nz = normal.getZ(i) * 0.3;
    const length = Math.hypot(nx, ny, nz) || 1;
    normal.setXYZ(i, nx / length, ny / length, nz / length);
  }
  normal.needsUpdate = true;
  return merged;
}

function mergeAll(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const merged = parts[0] as THREE.BufferGeometry;
  const positionArrays: number[] = [];
  for (const part of parts) {
    const attribute = part.getAttribute("position") as THREE.BufferAttribute;
    for (let i = 0; i < attribute.count; i++) {
      positionArrays.push(attribute.getX(i), attribute.getY(i), attribute.getZ(i));
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positionArrays, 3));
  geometry.computeVertexNormals();
  merged.dispose();
  return geometry;
}

export function buildTufts(
  rng: Rng,
  exclude?: (x: number, z: number) => boolean,
): THREE.InstancedMesh {
  const geometry = tuftGeometry(rng);
  const material = smooth(Tone.stone);
  const total = scatter(WORLD.tufts);
  const tufts = new THREE.InstancedMesh(geometry, material, total);
  tufts.castShadow = false;
  tufts.receiveShadow = true;

  const dummy = new THREE.Object3D();
  const color = new THREE.Color();
  const radius = WORLD.groundSize * 0.5;

  let placed = 0;
  for (let i = 0; i < total; i++) {
    const x = rng.range(-radius, radius);
    const z = rng.range(-radius, radius);
    const y = terrainHeight(x, z);
    dummy.position.set(x, y, z);
    dummy.rotation.y = rng.range(0, Math.PI * 2);
    dummy.rotation.x = rng.range(-0.08, 0.08);
    dummy.rotation.z = rng.range(-0.08, 0.08);
    const scale = rng.range(1.2, 2.6);
    dummy.scale.set(scale, rng.range(0.75, 1.5) * scale, scale);
    dummy.updateMatrix();

    color.set(shade(Tone.stone, rng.range(-0.16, 0.12)));
    if (exclude?.(x, z)) continue;
    tufts.setMatrixAt(placed, dummy.matrix);
    tufts.setColorAt(placed, color);
    placed++;
  }
  tufts.count = placed;

  tufts.userData.slot = slotNormal(Slot.grass);
  tufts.userData.variant = rng.range(0.15, 0.8);
  tufts.instanceMatrix.needsUpdate = true;
  if (tufts.instanceColor) tufts.instanceColor.needsUpdate = true;
  return tufts;
}

export function buildLeaves(rng: Rng): THREE.InstancedMesh {
  const geometry = new THREE.PlaneGeometry(1, 1);
  geometry.rotateX(-Math.PI / 2);
  const total = scatter(WORLD.leaves);
  const leaves = new THREE.InstancedMesh(geometry, flat(Tone.stone), total);
  leaves.receiveShadow = true;
  leaves.castShadow = false;

  const dummy = new THREE.Object3D();
  const color = new THREE.Color();
  const radius = WORLD.groundSize * 0.5;

  for (let i = 0; i < total; i++) {
    const x = rng.range(-radius, radius);
    const z = rng.range(-radius, radius);
    dummy.position.set(x, terrainHeight(x, z) + 0.012, z);
    dummy.rotation.set(rng.range(-0.42, 0.42), rng.range(0, Math.PI * 2), rng.range(-0.42, 0.42));
    const size = rng.range(WORLD.leafSize[0], WORLD.leafSize[1]);
    dummy.scale.set(size, 1, size * rng.range(0.5, 1));
    dummy.updateMatrix();
    leaves.setMatrixAt(i, dummy.matrix);
    color.set(shade(Tone.stone, rng.range(-0.34, 0.1)));
    leaves.setColorAt(i, color);
  }

  leaves.userData.slot = slotNormal(Slot.litter);
  leaves.userData.variant = rng.range(0.3, 0.85);
  leaves.instanceMatrix.needsUpdate = true;
  if (leaves.instanceColor) leaves.instanceColor.needsUpdate = true;
  return leaves;
}

export function buildRocks(rng: Rng): THREE.Group {
  const geometry = new THREE.DodecahedronGeometry(0.28, 0);
  const total = scatter(WORLD.rocks);
  const rocks = new THREE.InstancedMesh(geometry, flat(Tone.stone), total);
  rocks.castShadow = true;
  rocks.receiveShadow = true;

  const dummy = new THREE.Object3D();
  const color = new THREE.Color();
  const radius = WORLD.groundSize * 0.5;

  for (let i = 0; i < total; i++) {
    const x = rng.range(-radius, radius);
    const z = rng.range(-radius, radius);
    dummy.position.set(x, terrainHeight(x, z) + 0.04, z);
    dummy.rotation.set(rng.range(0, Math.PI), rng.range(0, Math.PI), rng.range(0, Math.PI));
    const scale = rng.range(0.3, 1.5);
    dummy.scale.set(scale, scale * rng.range(0.5, 0.9), scale);
    dummy.updateMatrix();
    rocks.setMatrixAt(i, dummy.matrix);
    color.set(shade(Tone.stone, rng.range(-0.2, 0.2)));
    rocks.setColorAt(i, color);
  }

  rocks.userData.slot = slotNormal(Slot.stone);
  rocks.userData.variant = 0.5;
  rocks.instanceMatrix.needsUpdate = true;
  if (rocks.instanceColor) rocks.instanceColor.needsUpdate = true;
  return group([rocks]);
}
